import { Component, effect, input, Input, OnDestroy, signal } from '@angular/core';
import { Car } from '../../models/car-model';
import { NavigationStart, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { likeCar } from '../../store/actions/car.actions';
import { selectAuthUser } from '../../store/selectors/auth.selectors';
import { Statuses } from '../../shared/constants/status.constants';
import { filter, Subject, takeUntil } from 'rxjs';
declare var bootstrap: any;

@Component({
  selector: 'car-images-carousel',
  standalone: false,
  templateUrl: './car-images-carousel.component.html',
  styleUrl: './car-images-carousel.component.scss',
})
export class CarImagesCarouselComponent implements OnDestroy {
  car = input<Car | undefined>(undefined);
  @Input() carouselId: string = 'carouselCar';
  @Input() imagClass: string = 'cover';
  @Input() currentImageIndex: number = 0;
  @Input() displayIndicator = false;
  @Input() displayLikeButton = true;
  @Input() listingCard = false;

  localCar = signal<Car | undefined>(undefined);

  currentImage = '';
  modalInstance: any;

  activeIndex = 0;
  visibleCount = 4;
  thumbWidth = 68; // 60px + 8px gap
  translateX = -50;

  statuses = Statuses;

  userId: number | undefined;

  private destroy$ = new Subject<void>();
  private listingId?: number;

  constructor(
    private router: Router,
    private store: Store
  ) {
    this.store
      .select(selectAuthUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.userId = user?.id;
      });
    effect(() => {
      const listing = this.car();
      this.localCar.set(listing);
      if (listing?.id !== this.listingId) {
        this.listingId = listing?.id;
        this.activeIndex = 0;
        this.currentImageIndex = 0;
        this.translateX = -50;
        this.currentImage = listing?.pictures?.[0] ?? '';
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.closeModal();
    });
  }

  closeModal() {
    const modalEl = document.getElementById('imageModal');
    if (modalEl) {
      const modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
      modal.hide();
    }
  }

  onSlideChanged(event: Event): void {
    this.activeIndex = (event as Event & { to: number }).to;
    this.currentImageIndex = this.activeIndex;
    this.updateSliderPosition();
  }

  openModal(index: number) {
    this.currentImageIndex = index;
    this.loadCurrent();

    const modalEl = document.getElementById('imageModal');
    if (!modalEl) return;

    // Reuse existing instance if it exists
    this.modalInstance = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);

    // Only show if not already shown
    if (!modalEl.classList.contains('show')) {
      this.modalInstance.show();
    }
  }

  // Needed for not jumping to the details page
  prev(event: MouseEvent) {
    const pictures = this.car()?.pictures;
    if (pictures) {
      event.stopPropagation();
      this.currentImageIndex = (this.currentImageIndex - 1 + pictures.length) % pictures.length;
      this.loadCurrent();
    }
  }

  // Needed for not jumping to the details page
  next(event: MouseEvent) {
    const pictures = this.car()?.pictures;
    if (pictures) {
      event.stopPropagation();
      this.currentImageIndex = (this.currentImageIndex + 1) % pictures.length;
      this.loadCurrent();
    }
  }

  // Needed for not jumping to the details page
  toggleLike(event: MouseEvent) {
    event.preventDefault();
    const car = this.car();
    if (car !== undefined) {
      event.stopPropagation();
      if (!this.userId) {
        this.router.navigate(['/login']);
      } else {
        if (this.isLiked()) {
          this.likeCar(false);
        } else {
          this.likeCar(true);
        }
      }
    }
  }

  onThumbClick(index: number) {
    this.activeIndex = index;
    this.updateSliderPosition();
  }

  updateSliderPosition() {
    const track = document.querySelector('.thumbs-track') as HTMLElement;
    const firstItem = track?.children[0] as HTMLElement;

    if (!track || !firstItem) return;

    const itemWidth = firstItem.getBoundingClientRect().width;

    const maxTranslate = track.scrollWidth - track.parentElement!.clientWidth;

    let desired = this.activeIndex * itemWidth;
    this.translateX = Math.min(desired - 60, maxTranslate);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private likeCar(isLike: boolean) {
    const car = this.localCar();
    if (car?.id && this.userId) {
      this.store.dispatch(likeCar({ userId: this.userId, carId: car.id, isLike: isLike }));
    }
  }

  isLiked(): boolean {
    return this.localCar()?.liked === true;
  }

  private loadCurrent() {
    const pictures = this.car()?.pictures;
    if (pictures) {
      this.currentImage = pictures[this.currentImageIndex];
    }
  }
}
